import { Minus, Plus, RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { Button } from './button';
import { DialogBody, DialogFooter } from './dialog';

export interface CroppedBlobResult {
	/** The cropped image blob */
	croppedBlob: Blob;
	/** Object URL for immediate preview */
	previewUrl: string;
}

/**
 * Non-destructive crop geometry emitted in "coordinates" mode. x/y/width/height
 * are percentages of the original image (0–100); the caller stores these
 * against the image instead of baking a new blob.
 */
export interface ImageCropCoords {
	x: number;
	y: number;
	width: number;
	height: number;
	zoom: number;
	rotation: number;
	/** e.g. "1:1" */
	aspectRatio: string;
}

interface ImageCropperProps {
	/** A concrete, already-loaded image source (data URL or plain URL). */
	imageSrc: string;
	/**
	 * Coordinates mode: emit the crop rect (percentages) for non-destructive,
	 * display-time cropping. Provide either this or `onSaveBlob`.
	 */
	onSaveCrop?: (coords: ImageCropCoords) => void;
	/** Blob mode: bake and return a cropped image blob (logos/avatars). */
	onSaveBlob?: (result: CroppedBlobResult) => void;
	onCancel: () => void;
	/** Aspect ratio of the crop area. Default: 1 (square). */
	aspect?: number;
	cropShape?: 'rect' | 'round';
	/** Size of the crop area in pixels. Default: 260. */
	cropSize?: number;
	saveLabel?: string;
	cancelLabel?: string;
	/** Restore a previously saved crop (coordinates mode). */
	initialCrop?: ImageCropCoords | null;
	/** Hide the rotate control (rotation isn't applied at display time). */
	hideRotate?: boolean;
	/** Keep the crop area within the image bounds. Default: false. */
	restrictPosition?: boolean;
	/** External busy flag (e.g. mutation in flight) — disables the save button. */
	saving?: boolean;
	/** Node rendered on the left of the footer (e.g. a "Change image" button). */
	secondaryAction?: React.ReactNode;
}

/**
 * Headless crop editor: the react-easy-crop canvas + zoom/rotate controls + a
 * Cancel/Save footer. Chrome-free so it can live inside a Dialog
 * ({@link ImageCropModal}) or inline inside another surface (e.g. the product
 * Edit modal, avoiding a modal-on-modal stack).
 */
export function ImageCropper({
	imageSrc,
	onSaveCrop,
	onSaveBlob,
	onCancel,
	aspect = 1,
	cropShape = 'rect',
	cropSize = 260,
	saveLabel = 'Save',
	cancelLabel = 'Cancel',
	initialCrop,
	hideRotate = false,
	restrictPosition = false,
	saving = false,
	secondaryAction,
}: ImageCropperProps) {
	const coordinatesMode = !!onSaveCrop;
	const [crop, setCrop] = useState({ x: 0, y: 0 });
	const [zoom, setZoom] = useState(initialCrop?.zoom || 1);
	const [rotation, setRotation] = useState(0);
	const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
	const [croppedAreaPercent, setCroppedAreaPercent] = useState<Area | null>(null);

	const onCropComplete = useCallback((percent: Area, pixels: Area) => {
		setCroppedAreaPixels(pixels);
		setCroppedAreaPercent(percent);
	}, []);

	const handleSave = async () => {
		if (coordinatesMode) {
			if (!croppedAreaPercent) return;
			onSaveCrop!({
				x: croppedAreaPercent.x,
				y: croppedAreaPercent.y,
				width: croppedAreaPercent.width,
				height: croppedAreaPercent.height,
				zoom,
				rotation,
				aspectRatio: formatAspect(aspect),
			});
			return;
		}
		if (!croppedAreaPixels || !onSaveBlob) return;
		const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels, rotation);
		const previewUrl = URL.createObjectURL(croppedBlob);
		onSaveBlob({ croppedBlob, previewUrl });
	};

	const cropW = cropSize;
	const cropH = Math.round(cropSize / aspect);

	return (
		<>
			<DialogBody className="px-0 pb-0">
				<div className="relative" style={{ height: 380 }}>
					<Cropper
						image={imageSrc}
						crop={crop}
						zoom={zoom}
						rotation={rotation}
						aspect={aspect}
						cropShape={cropShape}
						showGrid
						onCropChange={setCrop}
						onZoomChange={setZoom}
						onCropComplete={onCropComplete}
						minZoom={0.5}
						maxZoom={4}
						cropSize={{ width: cropW, height: cropH }}
						restrictPosition={restrictPosition}
						initialCroppedAreaPercentages={
							initialCrop
								? {
										x: initialCrop.x,
										y: initialCrop.y,
										width: initialCrop.width,
										height: initialCrop.height,
									}
								: undefined
						}
						style={{
							containerStyle: { backgroundColor: 'rgba(0,0,0,0.85)' },
							cropAreaStyle: {
								border: '1px solid rgba(255,255,255,0.15)',
								borderRadius: cropShape === 'round' ? '50%' : 12,
							},
						}}
					/>
				</div>

				{/* Controls bar */}
				<div className="flex items-center gap-3 px-4 py-3">
					<div className="flex flex-1 items-center gap-2">
						<Minus size={12} className="shrink-0 text-text-4" />
						<input
							type="range"
							min={0.5}
							max={4}
							step={0.01}
							value={zoom}
							onChange={(e) => setZoom(parseFloat(e.target.value))}
							className="h-1 flex-1 cursor-pointer appearance-none rounded-full bg-line-2 accent-[var(--color-accent)] [&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[var(--color-accent)]"
						/>
						<Plus size={12} className="shrink-0 text-text-4" />
					</div>

					{!hideRotate && (
						<button
							type="button"
							onClick={() => setRotation((r) => (r - 90 + 360) % 360)}
							className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-line-2 text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1"
							title="Rotate left"
						>
							<RotateCcw size={14} />
						</button>
					)}
				</div>
			</DialogBody>

			<DialogFooter>
				{secondaryAction ?? <span className="hidden sm:block" />}
				<div className="flex flex-col-reverse gap-2 sm:flex-row">
					<Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
						{cancelLabel}
					</Button>
					<Button type="button" onClick={handleSave} disabled={saving}>
						{saving ? 'Saving…' : saveLabel}
					</Button>
				</div>
			</DialogFooter>
		</>
	);
}

/* ── Helpers ── */

/** Format a numeric aspect ratio into a "w:h" label (e.g. 1 → "1:1"). */
function formatAspect(aspect: number): string {
	const known: Array<[number, string]> = [
		[1, '1:1'],
		[4 / 3, '4:3'],
		[3 / 2, '3:2'],
		[16 / 9, '16:9'],
		[3 / 4, '3:4'],
		[2 / 3, '2:3'],
		[9 / 16, '9:16'],
	];
	for (const [value, label] of known) {
		if (Math.abs(aspect - value) < 0.01) return label;
	}
	return `${Math.round(aspect * 100) / 100}:1`;
}

function createImage(url: string): Promise<HTMLImageElement> {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.addEventListener('load', () => resolve(img));
		img.addEventListener('error', reject);
		img.crossOrigin = 'anonymous';
		img.src = url;
	});
}

async function getCroppedImg(imageSrc: string, crop: Area, rotation = 0): Promise<Blob> {
	const image = await createImage(imageSrc);
	const canvas = document.createElement('canvas');
	const ctx = canvas.getContext('2d')!;

	const maxSize = Math.max(image.width, image.height);
	const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

	canvas.width = safeArea;
	canvas.height = safeArea;

	ctx.translate(safeArea / 2, safeArea / 2);
	ctx.rotate((rotation * Math.PI) / 180);
	ctx.translate(-safeArea / 2, -safeArea / 2);
	ctx.drawImage(image, safeArea / 2 - image.width / 2, safeArea / 2 - image.height / 2);

	const data = ctx.getImageData(0, 0, safeArea, safeArea);

	canvas.width = crop.width;
	canvas.height = crop.height;

	ctx.putImageData(
		data,
		Math.round(0 - safeArea / 2 + image.width / 2 - crop.x),
		Math.round(0 - safeArea / 2 + image.height / 2 - crop.y)
	);

	return new Promise((resolve, reject) => {
		canvas.toBlob((blob) => {
			if (blob) resolve(blob);
			else reject(new Error('Canvas is empty'));
		}, 'image/png');
	});
}
